"use client";

import { useCallback } from "react";
import useSWR, { useSWRConfig, type SWRConfiguration } from "swr";
import { useStore } from "@/app/context/StoreContext";
import { useDataRefresh, type DataRefreshPolicy } from "./use-data-refresh";
import { ApiError } from "@/lib/api/client";
import { dataCacheIdentity, dataCacheKey } from "@/lib/api/data-cache-identity";
import { API_RESPONSE_CACHE_ENDPOINTS } from "@/lib/api-response-cache-kinds";

const VERSIONED_PATHS = new Set(Object.values(API_RESPONSE_CACHE_ENDPOINTS));
export const TABLE_VERSION_CHECK_INTERVAL_MS = 30_000;

function accessError(error: unknown): boolean {
  return error instanceof ApiError && [401, 403, 404].includes(error.status);
}

/**
 * SWR остаётся единственным владельцем данных. Fetcher получает прежний объект
 * для If-None-Match; 304 возвращает тот же объект и не перестраивает таблицу.
 * Непрозрачная метка связана с объектом через WeakMap, без второй RAM-копии.
 * Ключ включает user/account и исходный URL со store-scope. null по-прежнему
 * отключает запросы (shared, скрытые источники, ещё не готовый scope).
 * Default live policy is unchanged. Daily history opts out of automatic reads.
 * Advertising snapshots keep mount/reconnect validation, but no focus reads or
 * version timer; their source-specific deduplication interval stays explicit.
 */
export function useTableDataSWR<T>(
  url: string | null,
  fetcher: (url: string, previousData?: unknown, identity?: string) => Promise<T>,
  options: SWRConfiguration<T> = {},
  policy: DataRefreshPolicy = "live",
) {
  const { cache } = useSWRConfig();
  const { user, account } = useStore();
  const identity = dataCacheIdentity(user?.id, account?.id);
  const key = url ? dataCacheKey(url, identity) : null;
  const versioned = !!url && VERSIONED_PATHS.has(url.split("?")[0]);
  const fetch = useCallback(() => fetcher(url!, key ? cache.get(key)?.data : undefined, identity), [cache, fetcher, identity, key, url]);
  const daily = policy === "daily";
  const snapshot = policy === "snapshot";
  const swr = useSWR<T>(key, fetch, {
    ...options,
    revalidateOnFocus: !daily && !snapshot,
    revalidateOnReconnect: !daily,
    revalidateIfStale: !daily,
    focusThrottleInterval: 5_000,
    refreshInterval: policy === "live" && versioned ? TABLE_VERSION_CHECK_INTERVAL_MS : 0,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    ...(daily || snapshot ? { keepPreviousData: false } : {}),
    ...(daily ? { dedupingInterval: 15_000 } : {}),
    // Preserve an unchanged 304 object; a changed source version replaces it.
    ...(versioned || daily ? { compare: Object.is } : {}),
    shouldRetryOnError: daily ? options.shouldRetryOnError ?? false : error => !accessError(error),
  });
  useDataRefresh(key, swr.mutate, policy);

  // SWR обычно сохраняет прошлые данные при ошибке. При отзыве доступа их
  // нельзя продолжать показывать. Сетевой сбой, напротив, не стирает таблицу.
  return accessError(swr.error) ? { ...swr, data: undefined } : swr;
}
